import { useMemo, useState } from "react";
import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import { projectCategories, projectGallery } from "@/mocks/projects";

export default function Projects() {
  const [filter, setFilter] = useState<string>("All");

  const filtered = useMemo(() => {
    if (filter === "All") return projectGallery;
    return projectGallery.filter((project) => project.category === filter);
  }, [filter]);

  const tabs = ["All", ...projectCategories];

  return (
    <>
      <Seo
        title="Projects & Applications | KAL LORDS ZERO21 DJT EV Retrofit"
        description="EV retrofit projects and applications across passenger mobility, commercial mobility, cargo, fleet electrification and special applications. Project details coming soon."
        keywords="EV retrofit projects, fleet electrification projects, commercial vehicle conversion"
        path="/projects"
      />
      <PageHero
        eyebrow="Projects"
        breadcrumb="Projects"
        title="Applications Across Mobility"
        description="These are the application categories where retrofit is being considered. Specific customer names, vehicle counts, performance figures and project results are published only when verified."
      />

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Applications"
              title="Explore by Category"
              description="Filter the applications below. All project entries are marked as details coming soon until verified information is available."
            />
          </Reveal>

          <Reveal delay={0.08}>
            <div className="mt-8 flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const isActive = filter === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFilter(tab)}
                    aria-pressed={isActive}
                    className={`cursor-pointer rounded-full border px-4 py-2 font-label text-xs font-semibold transition-colors ${
                      isActive
                        ? "border-foreground-950 bg-foreground-950 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((project, index) => (
              <Reveal key={project.key} delay={index * 0.05}>
                <article className="flex h-full flex-col rounded-lg border border-background-200 bg-background-100 p-6 transition-colors duration-300 hover:border-primary-300">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-700">
                      <i className={`${project.icon} text-xl`} />
                    </span>
                    <span className="rounded-full bg-secondary-100 px-3 py-1 font-label text-[10px] font-semibold uppercase tracking-[0.12em] text-secondary-900">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-foreground-950">{project.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {project.description}
                  </p>
                  <p className="mt-4 flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-500">
                    <i className="ri-time-line" />
                    {project.status}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 p-10 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-background-50 text-foreground-500">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <p className="mt-4 text-sm font-semibold text-foreground-800">
                No projects in this category yet
              </p>
              <p className="mt-1 text-sm text-foreground-500">
                Details will be published as verified information becomes available.
              </p>
            </div>
          ) : null}

          <Reveal delay={0.1}>
            <div className="mt-14 rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <SectionHeading
                  eyebrow="Your Application"
                  title="Have a Vehicle or Application to Discuss?"
                  description="Share your vehicle details or fleet requirement and the team will review what is technically possible."
                />
                <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
                  <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="md" icon="ri-flashlight-line">
                    Check Eligibility
                  </ActionButton>
                  <ActionButton to="/fleet" variant="outline" size="md" icon="ri-roadster-line">
                    Fleet Assessment
                  </ActionButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}